import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../services/api';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Typography, Autocomplete } from '@mui/material';
const DeleteIcon = apiService.getBlobAssetUrl('delete_A1.svg');
const AddIcon = apiService.getBlobAssetUrl('Add_Icon.svg');

const statusTypeShape = PropTypes.shape({
  ID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  Name: PropTypes.string.isRequired,
});

const AddProjectDialog = ({ open, onClose, onSave, projectStatuses, projectTypes, practices }) => {
  const initialProjectState = {
    name: '',
    status: '',
    type: '',
    startDate: '',
    endDate: '',
    description: '',
    practice: '',
    client: '',
  };

  const initialContacts = [{ id: `${Date.now()}-0`, fullName: '', email: '' }];

  const [project, setProject] = useState(initialProjectState);
  const [contacts, setContacts] = useState(initialContacts);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [startDateError, setStartDateError] = useState(null);
  const [endDateError, setEndDateError] = useState(null);
  const [contactErrors, setContactErrors] = useState({});

  // Fetch clients data on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await apiService.fetchData('clients/details');
        const clientData = Array.isArray(response) ? response : response.results || [];
        setClients(clientData);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load clients. Please try again.');
      }
    };
    if (open) {
      fetchClients();
    }
  }, [open]);

  const validateProjectName = async (projectName, setNameError) => {
    const trimmedName = projectName.trim();
    const validPattern = /^[a-zA-Z0-9\s_-]+$/;

    if (!trimmedName) {
      setNameError("Project name can't be empty.");
      return false;
    }

    if (!validPattern.test(trimmedName)) {
      setNameError('Special characters (e.g., !@#$%^&*()) are not allowed in the project name.');
      return false;
    }

    try {
      const userId = sessionStorage.getItem('user_id');
      const practiceId = sessionStorage.getItem('practice_id');
      if (!userId || !practiceId) {
        setNameError('User ID or Practice ID is not available. Please log in again.');
        return false;
      }
      const response = await apiService.fetchData(`projects/${userId}/${practiceId}/survey`);
      const projects = response.results || [];
      const exists = projects.some((p) => p.Name.toLowerCase() === trimmedName.toLowerCase());
      if (exists) {
        setNameError('A project with this name already exists.');
        return false;
      }
      setNameError(null);
      return true;
    } catch (err) {
      setNameError('Error checking project name. Please try again.');
      console.error('Error checking project name:', err);
      return false;
    }
  };

  const validateDatesByStatus = (status, startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    setStartDateError(null);
    setEndDateError(null);

    if (!status) {
      if (!startDate) setStartDateError('Start date is required.');
      if (!endDate) setEndDateError('End date is required.');
      return false;
    }

    if (!start) {
      setStartDateError('Start date is required.');
      return false;
    }
    if (!end) {
      setEndDateError('End date is required.');
      return false;
    }
    if (end <= start) {
      setEndDateError('End date must be after start date.');
      return false;
    }

    const todayFormatted = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const statusLower = status.toLowerCase();

    if (statusLower === 'in progress') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for In Progress projects.`);
        return false;
      }
      if (end <= today) {
        setEndDateError(`End date must be after today (${todayFormatted}) for In Progress projects.`);
        return false;
      }
    } else if (statusLower === 'completed') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for Completed projects.`);
        return false;
      }
      if (end >= today) {
        setEndDateError(`End date must be before today (${todayFormatted}) for Completed projects.`);
        return false;
      }
    } else if (statusLower === 'cancelled') {
      if (start >= today) {
        setStartDateError(`Start date must be before today (${todayFormatted}) for Cancelled projects.`);
        return false;
      }
    }

    return true;
  };

  const handleInputChange = async (e, newValue, name) => {
    if (name === 'client') {
      setProject((prev) => ({ ...prev, client: newValue ? newValue.Name : '' }));
      
      // Fetch client contacts when client is selected
      if (newValue && newValue.Name) {
        try {
          const response = await apiService.fetchData(`clients/${newValue.Name}`);
          if (Array.isArray(response) && response.length > 0) {
            const clientContacts = response.map((contact, index) => ({
              id: `${Date.now()}-${index}`,
              fullName: contact.OwnerName || '',
              email: contact.OwnerEmail || ''
            }));
            setContacts(clientContacts);
            setContactErrors({});
          } else {
            setContacts(initialContacts);
          }
        } catch (err) {
          console.error('Error fetching client contacts:', err);
          setContacts(initialContacts);
        }
      } else {
        setContacts(initialContacts);
      }
    } else {
      const { name: inputName, value } = e.target;
      console.log(`Input changed: ${inputName} = ${value}`);

      let updatedProject = { ...project, [inputName]: value };

      if (inputName === 'startDate' || inputName === 'endDate' || inputName === 'status') {
        // Validate dates whenever startDate, endDate, or status changes
        const newStatus = inputName === 'status' ? value : project.status;
        const newStartDate = inputName === 'startDate' ? value : project.startDate;
        const newEndDate = inputName === 'endDate' ? value : project.endDate;

        if (inputName === 'startDate' && value && project.endDate) {
          // Reset endDate if it's before the new startDate
          const start = new Date(value);
          const end = new Date(project.endDate);
          if (end <= start) {
            updatedProject.endDate = '';
          }
        }

        validateDatesByStatus(newStatus, newStartDate, newEndDate);
      }

      setProject(updatedProject);

      if (inputName === 'name') {
        await validateProjectName(value, setNameError);
      }
    }
  };

  const handleContactChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...contacts];
    updatedContacts[index][name] = value;

    const currentErrors = contactErrors[index] || {};

    if (name === 'fullName') {
      const trimmedValue = value.trim();
      const namePattern = /^[a-zA-Z0-9\s_-]+$/;
      if (!trimmedValue) {
        currentErrors.fullName = 'Full Name cannot be empty.';
      } else if (!namePattern.test(trimmedValue)) {
        currentErrors.fullName = 'Full Name can only contain letters, numbers, spaces, hyphens, or underscores.';
      } else {
        currentErrors.fullName = null;
        const currentContact = updatedContacts[index];
        if (currentContact.email && currentContact.fullName) {
          const isDuplicate = updatedContacts.some((contact, i) =>
            i !== index && contact.fullName.trim() === trimmedValue && contact.email === currentContact.email
          );
          if (isDuplicate) {
            currentErrors.duplicate = 'A contact with this name and email already exists.';
          } else {
            currentErrors.duplicate = null;
          }
        }
      }
    } else if (name === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailPattern.test(value)) {
        currentErrors.email = 'Please enter a valid email address (e.g., user@example.com).';
      } else {
        currentErrors.email = null;
        const currentContact = updatedContacts[index];
        if (currentContact.fullName && currentContact.email) {
          const isDuplicate = updatedContacts.some((contact, i) =>
            i !== index && contact.fullName.trim() === currentContact.fullName.trim() && contact.email === currentContact.email
          );
          if (isDuplicate) {
            currentErrors.duplicate = 'A contact with this name and email already exists.';
          } else {
            currentErrors.duplicate = null;
          }
        }
      }
    }

    setContactErrors((prev) => ({
      ...prev,
      [index]: currentErrors.fullName || currentErrors.email || currentErrors.duplicate ? currentErrors : null,
    }));

    setContacts(updatedContacts);
  };

  const handleAddNewContact = () => {
    setContacts([...contacts, { id: `${Date.now()}-${contacts.length}`, fullName: '', email: '' }]);
  };

  const handleDeleteContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    
    // If no contacts remain, add one empty contact
    if (updatedContacts.length === 0) {
      setContacts(initialContacts);
      setContactErrors({});
    } else {
      setContacts(updatedContacts);
      
      // Clear errors for the deleted contact and reindex remaining errors
      const newContactErrors = {};
      Object.keys(contactErrors).forEach((key) => {
        const errorIndex = parseInt(key);
        if (errorIndex < index) {
          newContactErrors[errorIndex] = contactErrors[errorIndex];
        } else if (errorIndex > index) {
          newContactErrors[errorIndex - 1] = contactErrors[errorIndex];
        }
      });
      setContactErrors(newContactErrors);
    }
    setError(null);
  };

  const handleCancel = () => {
    setProject(initialProjectState);
    setContacts(initialContacts);
    setError(null);
    setNameError(null);
    setStartDateError(null);
    setEndDateError(null);
    setContactErrors({});
    onClose();
  };

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const selectedStatus = projectStatusOptions.find((status) => status.Name === project.status);
      const selectedType = projectTypes?.find((type) => type.Name === project.type);
      const selectedPractice = practices.find((practice) => practice.Name === project.practice);
      const selectedClient = clients.find((client) => client.Name === project.client);

      if (nameError) {
        throw new Error(nameError);
      }

      if (!selectedStatus || !selectedType || !selectedPractice || !selectedClient) {
        throw new Error('Please select a valid project status, type, practice, and client.');
      }

      if (!validateDatesByStatus(project.status, project.startDate, project.endDate)) {
        throw new Error('Please fix date errors before saving.');
      }

      const invalidEmails = contacts.some((contact, index) => contact.email && contactErrors[index]);
      if (invalidEmails) {
        throw new Error('Please fix all invalid email addresses before saving.');
      }

      const seen = new Set();
      const hasDuplicates = contacts.some((contact) => {
        const key = `${contact.fullName}-${contact.email}`;
        if (seen.has(key) && contact.fullName && contact.email) {
          return true;
        }
        seen.add(key);
        return false;
      });
      if (hasDuplicates) {
        throw new Error('Duplicate contacts with the same name and email are not allowed.');
      }

      let formattedStartDate = null;
      let formattedEndDate = null;

      if (project.startDate) {
        try {
          const date = new Date(project.startDate);
          if (isNaN(date.getTime())) throw new Error('Invalid start date.');
          formattedStartDate = date.toISOString().split('T')[0];
        } catch (error) {
          throw new Error('Invalid start date format.');
        }
      }

      if (project.endDate) {
        try {
          const date = new Date(project.endDate);
          if (isNaN(date.getTime())) throw new Error('Invalid end date.');
          formattedEndDate = date.toISOString().split('T')[0];
        } catch (error) {
          throw new Error('Invalid end date format.');
        }
      }

      const userId = sessionStorage.getItem('user_id');
      const projectData = {
        project_data: {
          project_name: project.name,
          project_status: selectedStatus.ID,
          project_type: selectedType.ID,
          project_start_date: formattedStartDate,
          created_by: userId,
          last_modified_by: userId,
          end_date: formattedEndDate,
          project_description: project.description,
          practice_name: selectedPractice.Name,
          practice_id: selectedPractice.ID,
          client_name: selectedClient.Name,
          project_customer_contacts: contacts
            .filter((contact) => contact.fullName && contact.email)
            .map((contact) => ({
              id: contact.id,
              full_name: contact.fullName,
              email: contact.email,
            })),
        },
      };

      if (projectData.project_data.project_customer_contacts.length === 0) {
        throw new Error('At least one valid contact is required.');
      }

      await onSave(projectData);
      setProject(initialProjectState);
      setContacts(initialContacts);
      setError(null);
      setNameError(null);
      setStartDateError(null);
      setEndDateError(null);
      setContactErrors({});
      onClose();
    } catch (err) {
      console.error('Error preparing project:', err);
      setError(`Failed to save project: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const projectStatusOptions = [
    { ID: 1, Name: 'In Progress' },
    { ID: 2, Name: 'Completed' },
    { ID: 3, Name: 'Cancelled' }
  ];
  const safeTypes = Array.isArray(projectTypes) ? projectTypes : [];
  const safePractices = Array.isArray(practices) ? practices.sort((a, b) => a.Name.localeCompare(b.Name)) : [];
  const safeClients = Array.isArray(clients) ? clients : [];

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth className="Project-dialog">
      <DialogTitle className="Project-dialog-title">
        New Project Details
        <button className="Project-close-btn" onClick={handleCancel}>
          ×
        </button>
      </DialogTitle>
      <DialogContent className="Project-dialog-content">
        {error && <Typography color="error">{error}</Typography>}
        <Autocomplete
          name="client"
          options={safeClients}
          getOptionLabel={(option) => option.Name || ''}
          onChange={(e, newValue) => handleInputChange(e, newValue, 'client')}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Client"
              variant="outlined"
              fullWidth
              placeholder="Select Client"
              InputLabelProps={{ shrink: true }}
              required
              disabled={submitting || safeClients.length === 0}
            />
          )}
          sx={{ mb: 2 }}
        />

        <div className="Project-project-details-row Project-project-details-row--double">
          <div className="project-name-container">
            <TextField
              name="name"
              label="Project Name"
              variant="outlined"
              fullWidth
              placeholder="Enter project name (e.g., Website Redesign)"
              value={project.name}
              onChange={handleInputChange}
              onBlur={async (e) => {
                const projectName = e.target.value.trim();
                if (projectName) {
                  await validateProjectName(projectName, setNameError);
                } else {
                  setNameError('Project name can\'t be empty.');
                }
              }}
              InputLabelProps={{ shrink: true }}
              required
              disabled={submitting}
              error={!!nameError}
              helperText={nameError}
            />
          </div>
          <TextField
            name="practice"
            label="Project Practice"
            select
            variant="outlined"
            fullWidth
            value={project.practice}
            onChange={handleInputChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            required
            disabled={submitting || safePractices.length === 0}
          >
            <option value="">Select Project Practice</option>
            {safePractices.map((practice) => (
              <option key={practice.ID || `practice-${practice.Name}`} value={practice.Name}>
                {practice.Name}
              </option>
            ))}
          </TextField>
        </div>

        <div className="Project-project-details-row Project-project-details-row--double">
          <TextField
            name="status"
            label="Project Status"
            select
            variant="outlined"
            fullWidth
            value={project.status}
            onChange={handleInputChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            required
            disabled={submitting}
          >
            <option value="">Select Project Status</option>
            {projectStatusOptions.map((status) => (
              <option key={status.ID} value={status.Name}>
                {status.Name}
              </option>
            ))}
          </TextField>
          <TextField
            name="type"
            label="Project Type"
            select
            variant="outlined"
            fullWidth
            value={project.type}
            onChange={handleInputChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            required
            disabled={submitting || safeTypes.length === 0}
          >
            <option value="">Select Project Type</option>
            {safeTypes.map((type) => (
              <option key={type.ID || `type-${type.Name}`} value={type.Name}>
                {type.Name}
              </option>
            ))}
          </TextField>
        </div>

        <div className="Project-project-details-row Project-project-details-row--double">
          <div style={{ flex: 1 }}>
            <TextField
              name="startDate"
              label="Project Start Date"
              type="date"
              variant="outlined"
              fullWidth
              value={project.startDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              required
              disabled={submitting}
              error={!!startDateError}
              helperText={startDateError}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              name="endDate"
              label="Project End Date"
              type="date"
              variant="outlined"
              fullWidth
              value={project.endDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              required
              disabled={!project.startDate || submitting}
              min={project.startDate || undefined}
              error={!!endDateError}
              helperText={endDateError}
            />
          </div>
        </div>

        <TextField
          name="description"
          label="Project Description"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          placeholder="Describe the project (e.g., goals, scope)"
          value={project.description}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          required
          disabled={submitting}
        />

        <Typography variant="h6" className="Project-dialog-section-title">Contacts</Typography>

        {contacts.map((contact, index) => (
          <div className="Project-contact-row" key={contact.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <TextField
                name="fullName"
                label="Full Name"
                variant="outlined"
                fullWidth
                value={contact.fullName}
                onChange={(e) => handleContactChange(index, e)}
                placeholder="Enter full name (e.g., John Doe)"
                InputLabelProps={{ shrink: true }}
                required
                disabled={submitting}
                error={!!contactErrors[index]?.fullName}
                helperText={contactErrors[index]?.fullName}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                name="email"
                label="Email ID"
                type="email"
                variant="outlined"
                fullWidth
                value={contact.email}
                onChange={(e) => handleContactChange(index, e)}
                placeholder="Enter email (e.g., john.doe@example.com)"
                InputLabelProps={{ shrink: true }}
                required
                disabled={submitting}
                error={!!contactErrors[index]?.email || !!contactErrors[index]?.duplicate}
                helperText={contactErrors[index]?.email || contactErrors[index]?.duplicate}
              />
            </div>
            <Button
              className="Project-delete-contact-button"
              onClick={() => handleDeleteContact(index)}
              disabled={submitting}
            >
              <img src={DeleteIcon} alt="Delete Icon" className="Project-delete-icon" />
            </Button>
          </div>
        ))}
        <Button className="Project-add-new-button" onClick={handleAddNewContact} disabled={submitting}>
          <img src={AddIcon} alt="Add Icon" className="Project-add-icon" /> Add New
        </Button>
      </DialogContent>

      <DialogActions className="Project-dialog-actions">
        <Button
          onClick={handleCancel}
          className="Project-cancel-button"
          disabled={submitting}
          sx={{
            background: 'none',
            border: 'none',
            color: '#0066D1',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 16px',
            '&:hover': {
              background: 'none',
              opacity: 0.8,
            },
            '&.Mui-disabled': {
              color: '#cccccc',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="Project-save-button"
          disabled={
            submitting ||
            !project.name ||
            !project.status ||
            !project.type ||
            !project.startDate ||
            !project.endDate ||
            !project.description ||
            !project.practice ||
            !project.client ||
            contacts.some((c) => !c.fullName || !c.email || (c.fullName && contactErrors[c.id])) ||
            safeTypes.length === 0 ||
            safePractices.length === 0 ||
            safeClients.length === 0 ||
            !!nameError ||
            !!startDateError ||
            !!endDateError
          }
          sx={{
            background: '#0066D1',
            color: 'white',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 32px',
            borderRadius: '8px',
            border: 'none',
            '&:hover': {
              background: '#034589',
            },
            '&.Mui-disabled': {
              background: '#cccccc',
              color: '#666666',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddProjectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  projectStatuses: PropTypes.arrayOf(statusTypeShape),
  projectTypes: PropTypes.arrayOf(statusTypeShape),
  practices: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    Name: PropTypes.string,
  })),
};

AddProjectDialog.defaultProps = {
  projectStatuses: [],
  projectTypes: [],
  practices: [],
};

export default AddProjectDialog;